import { createFileRoute } from '@tanstack/react-router'
import { useDebounce } from '@uidotdev/usehooks'
import { useMemo, useState } from 'react'

import { Loader } from '@/components/common/Loader'
import { LiffOutstandingBillCard } from '@/components/liff/outstanding/LiffOutstandingBillCard'
import { LiffShell } from '@/components/liff/LiffShell'
import { Button, Input } from '@/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import {
  useGetLiffOutstandingBills,
  useGetLiffOutstandingTotal,
} from '@/modules/bill/services/liff-outstanding'
import { formatNumber } from '@/utils/number'

export const Route = createFileRoute('/liff/outstanding/')({
  component: LiffOutstandingListPage,
})

function LiffOutstandingListPage() {
  const [tab, setTab] = useState<'buy' | 'sell'>('sell')
  const [location, setLocation] = useState<'' | BillLocationValue>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(search, 400)

  const listParams = useMemo(
    () => ({
      page,
      pageSize: 15,
      search: debouncedSearch || undefined,
      bill_type: tab,
      location: location || undefined,
    }),
    [page, debouncedSearch, tab, location],
  )

  const { data, isPending, isFetching } = useGetLiffOutstandingBills(listParams)
  const { data: totalData } = useGetLiffOutstandingTotal({
    search: debouncedSearch || undefined,
    bill_type: tab,
    location: location || undefined,
  })

  const rows = data?.data ?? []
  const meta = data?.meta
  const totalBills = totalData?.data?.total_bill ?? meta?.total ?? rows.length
  const totalOutstanding =
    totalData?.data?.total_remaining ?? totalData?.data?.total_price ?? 0

  const resetFilters = () => {
    setPage(1)
  }

  return (
    <LiffShell title="ใบเสร็จค้างชำระ">
      <div className="mx-auto max-w-md space-y-4">
        <div className="rounded-2xl bg-gradient-to-br from-rose-600 to-rose-700 p-5 text-white shadow-md">
          <p className="text-sm font-medium text-rose-100">ยอดค้างรวม (ตามตัวกรอง)</p>
          <p className="mt-1 text-3xl font-bold tracking-tight">
            {formatNumber(totalOutstanding)} <span className="text-lg font-semibold">บาท</span>
          </p>
          <p className="mt-2 text-sm text-rose-100">{totalBills} ใบ</p>
        </div>

        <div className="flex gap-2 rounded-xl bg-white p-1 shadow-sm">
          {(['sell', 'buy'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`min-h-[44px] flex-1 rounded-lg text-base font-semibold transition-colors ${
                tab === t ? 'bg-slate-800 text-white' : 'text-slate-600'
              }`}
              onClick={() => {
                setTab(t)
                resetFilters()
              }}
            >
              {t === 'sell' ? 'ขาย' : 'ซื้อ'}
            </button>
          ))}
        </div>

        <Select
          value={location || 'all'}
          onValueChange={(v) => {
            setLocation(v === 'all' ? '' : (v as BillLocationValue))
            resetFilters()
          }}
        >
          <SelectTrigger className="h-12 bg-white text-base">
            <SelectValue placeholder="ทุกสาขา" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-base py-3">
              ทุกสาขา
            </SelectItem>
            {BILL_LOCATION_VALUES.map((loc) => (
              <SelectItem key={loc} value={loc} className="text-base py-3">
                {BILL_LOCATION_LABELS[loc]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          className="h-12 bg-white text-base"
          placeholder="ค้นหาชื่อลูกค้า / เลขที่บิล"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            resetFilters()
          }}
        />

        {isPending ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <p className="rounded-2xl bg-white px-4 py-10 text-center text-slate-500">
            ไม่มีบิลค้างชำระ
          </p>
        ) : (
          <ul className="space-y-3">
            {rows.map((bill) => (
              <LiffOutstandingBillCard key={bill.id} bill={bill} />
            ))}
          </ul>
        )}

        {meta && meta.totalPages > 1 ? (
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 flex-1 text-base"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ก่อนหน้า
            </Button>
            <span className="flex min-h-[48px] items-center px-2 text-sm text-slate-600">
              {page} / {meta.totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              className="h-12 flex-1 text-base"
              disabled={page >= meta.totalPages || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              ถัดไป
            </Button>
          </div>
        ) : null}
      </div>
    </LiffShell>
  )
}
